/* Compiles per-group logical cells into the one grid aq-blocks actually has.
 *
 * There is a single grid. `resetGridArrays` sizes one `root.colSize`/`root.rowSize` from the
 * largest `col + colSpan` anywhere in the tree, and `placeNodes` indexes it with *every* node's
 * `layoutData`, at any depth, with no offset for the parent. Two rules follow, and they are the
 * whole of this file:
 *
 *   1. Odd indices are node tracks and even indices are the routing channels between them.
 *   2. A group's span has to enclose its descendants' cells.
 *
 * Numbering each group's children from that group's own origin breaks both: every group's first
 * child claims column 1, so the second group's children are drawn on top of the first group's, and
 * a group whose parent gave it a 1x1 cell never grows to hold them. That is ARD-33398, which
 * `grid` escaped by moving to the global pass (#33903) - the same defect is still live on the
 * `manual` path, which is the one Assistant Blocks uses.
 *
 * Authoring stays per-group, because that is the frame a person or a model thinks in - "put the
 * applications on the top row" is a sentence about one group. This turns that into global cells.
 *
 * Here rather than in `aq-blocks/layout`, and that is the point of it. `manual` takes global cells:
 * `fixupLocalManualLayout` treats whatever `layoutData` it finds as authoritative and only fills in
 * children that have none, and `placeNodes` indexes the one grid with every node's cell at any
 * depth. So the visualisation's contract is global positions, and supplying them is the caller's
 * job - which makes wanting to *speak* per-group an Assistant Blocks convenience rather than
 * anything the view knows about. The dependency runs one way: this reaches into aq-blocks, and
 * nothing in aq-blocks reaches back.
 *
 * Worth noting what that implies about the layout enumeration, since it is not obvious: nothing
 * here is specific to a model producing the cells. `interactions/edit.ts` sets
 * `layoutType = 'manual'` when a reader drags a node by hand, so hand-arranging and
 * assistant-arranging already reach the visualisation as the same thing - one global grid of
 * manual cells. The only difference is who computed them.
 */

import type { CellFill } from './layoutRecipe';

/** Where a node sits in its parent's own grid: 0-based, spans in whole cells. */
export type LogicalCell = {
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
  /** Fill the covered tracks along this axis, even from a single cell. */
  fill?: CellFill;
};

/** A node's cell in the one global grid, in the form `layoutData` takes. */
export type GlobalCell = {
  col: number;
  row: number;
  colSpan: number;
  rowSpan: number;
};

/**
 * The tree to compile, carrying whatever node type the caller holds.
 *
 * `cell` is where the node sits among its siblings. It is optional because a group's children are
 * not always all placed - the assistant positions the ones the sentence named - and an absent cell
 * is filled in below the placed ones rather than colliding with them.
 */
export type ManualTabularNode<T> = {
  node: T;
  cell?: LogicalCell;
  /** Center the children in the node's span when it is larger than they need. */
  centerContent?: boolean;
  children: ManualTabularNode<T>[];
};

/**
 * Two tracks of margin inside a group, on every side.
 *
 * One would be wrong rather than merely tight: a group starts on an odd track, so a single track of
 * margin would put its first child on an even one - a routing channel. Two keeps the parity, and
 * the even track it leaves is where an edge into the group's first child can run.
 */
const GROUP_MARGIN = 2;

/** A leaf is one track wide, which is the smallest odd extent there is. */
const LEAF_EXTENT = 1;

type Extent = { cols: number; rows: number };

/** The size of each logical column and row of a group's grid, in physical tracks. */
type ContentGrid = {
  colWidths: number[];
  rowHeights: number[];
  colStarts: number[];
  rowStarts: number[];
  extent: Extent;
};

const cellOf = <T>({ cell }: ManualTabularNode<T>): LogicalCell =>
  cell ?? { col: 0, row: 0, colSpan: 1, rowSpan: 1 };

const overlap = (a: LogicalCell, b: LogicalCell): boolean =>
  a.col < b.col + b.colSpan &&
  b.col < a.col + a.colSpan &&
  a.row < b.row + b.rowSpan &&
  b.row < a.row + a.rowSpan;

/**
 * Drops a cell that lands on one an earlier sibling already claimed.
 *
 * `withFilledCells` below keeps an *absent* cell off a placed one, which leaves the case where the
 * author names the same cell twice. Nothing upstream rejects it - the recipe parser clamps sign
 * and magnitude and span but never compares siblings - and two siblings with one cell compile to one
 * global cell and are drawn exactly on top of each other: one component invisible, with nothing
 * anywhere reporting it.
 *
 * First claim wins, so the result does not depend on which sibling is read second. Demoting the
 * loser to unplaced puts it on a fresh row below, which is a node in the wrong place rather than a
 * node that is not there.
 */
const withoutOverlappingCells = <T>(
  children: ManualTabularNode<T>[]
): ManualTabularNode<T>[] => {
  const claimed: LogicalCell[] = [];
  return children.map(child => {
    const { cell } = child;
    if (!cell) return child;
    if (claimed.some(other => overlap(other, cell))) {
      return { ...child, cell: undefined };
    }
    claimed.push(cell);
    return child;
  });
};

/**
 * Fills in a cell for every child that has none, on fresh rows under the placed ones.
 *
 * Below rather than beside, so a node the assistant did not mention can never take a cell it gave
 * to one it did.
 */
const withFilledCells = <T>(
  children: ManualTabularNode<T>[]
): ManualTabularNode<T>[] => {
  const placed = children.filter(child => child.cell);
  const unplaced = children.filter(child => !child.cell);
  if (unplaced.length === 0) return children;

  const firstFreeRow = placed.reduce((row, child) => {
    const { row: childRow, rowSpan } = cellOf(child);
    return Math.max(row, childRow + rowSpan);
  }, 0);

  /* Square-ish, which is what the grid layout does with an unarranged group. */
  const columnCount = Math.max(1, Math.ceil(Math.sqrt(unplaced.length)));
  const filled = new Map(
    unplaced.map((child, index) => [
      child,
      {
        col: index % columnCount,
        row: firstFreeRow + Math.floor(index / columnCount),
        colSpan: 1,
        rowSpan: 1,
      },
    ])
  );

  return children.map(child =>
    filled.has(child) ? { ...child, cell: filled.get(child) } : child
  );
};

/** Running start of each track, one channel between neighbours. Always lands even. */
const toStarts = (sizes: number[]): number[] => {
  const starts: number[] = [];
  let next = 0;
  for (const size of sizes) {
    starts.push(next);
    next += size + 1;
  }
  return starts;
};

/**
 * How wide each logical column has to be to hold what sits in it.
 *
 * Single-cell children set the width directly. A child spanning several columns is checked
 * afterwards and the shortfall given to the last column it covers, so the columns it does not
 * reach are not stretched by it.
 */
const toTrackSizes = (
  entries: { start: number; span: number; extent: number }[],
  trackCount: number
): number[] => {
  const sizes = new Array<number>(trackCount).fill(LEAF_EXTENT);

  for (const { start, span, extent } of entries) {
    if (span === 1) sizes[start] = Math.max(sizes[start], extent);
  }

  for (const { start, span, extent } of entries) {
    if (span === 1) continue;
    const covered =
      sizes.slice(start, start + span).reduce((sum, size) => sum + size, 0) +
      (span - 1);
    if (covered < extent) sizes[start + span - 1] += extent - covered;
  }

  return sizes;
};

/**
 * A node measured once, with the children it will be placed against.
 *
 * Both the sizing pass and the placing pass need each node's extent and each group's track sizes,
 * and both need the *same* filled child list - `withFilledCells` mints a fresh object for every
 * child it places, so calling it twice produces two sets of nodes for one logical child. Measuring
 * into this once and walking it to place is what keeps the compile linear: measuring on demand
 * cost 2 calls per child per pass, which is exponential in depth - 3.5 seconds at twelve levels.
 */
type Measured<T> = {
  source: ManualTabularNode<T>;
  /** In the order the grid was built from, each carrying the cell it was measured at. */
  children: Measured<T>[];
  grid: ContentGrid;
  extent: Extent;
};

const EMPTY_GRID: ContentGrid = {
  colWidths: [],
  rowHeights: [],
  colStarts: [],
  rowStarts: [],
  extent: { cols: LEAF_EXTENT, rows: LEAF_EXTENT },
};

const toContentGrid = <T>(children: Measured<T>[]): ContentGrid => {
  const columnEntries = children.map(({ source, extent }) => {
    const { col, colSpan } = cellOf(source);
    return { start: col, span: colSpan, extent: extent.cols };
  });
  const rowEntries = children.map(({ source, extent }) => {
    const { row, rowSpan } = cellOf(source);
    return { start: row, span: rowSpan, extent: extent.rows };
  });

  const columnCount = children.reduce((count, { source }) => {
    const { col, colSpan } = cellOf(source);
    return Math.max(count, col + colSpan);
  }, 0);
  const rowCount = children.reduce((count, { source }) => {
    const { row, rowSpan } = cellOf(source);
    return Math.max(count, row + rowSpan);
  }, 0);

  const colWidths = toTrackSizes(columnEntries, columnCount);
  const rowHeights = toTrackSizes(rowEntries, rowCount);
  const colStarts = toStarts(colWidths);
  const rowStarts = toStarts(rowHeights);

  const span = (starts: number[], sizes: number[]) =>
    starts.length === 0
      ? LEAF_EXTENT
      : starts[starts.length - 1] + sizes[sizes.length - 1];

  return {
    colWidths,
    rowHeights,
    colStarts,
    rowStarts,
    extent: {
      cols: span(colStarts, colWidths),
      rows: span(rowStarts, rowHeights),
    },
  };
};

/** One post-order pass: every node measured exactly once. */
const measure = <T>(source: ManualTabularNode<T>): Measured<T> => {
  if (source.children.length === 0) {
    return {
      source,
      children: [],
      grid: EMPTY_GRID,
      extent: { cols: LEAF_EXTENT, rows: LEAF_EXTENT },
    };
  }

  const children = withFilledCells(
    withoutOverlappingCells(source.children)
  ).map(measure);
  const grid = toContentGrid(children);

  return {
    source,
    children,
    grid,
    extent: {
      cols: grid.extent.cols + 2 * GROUP_MARGIN,
      rows: grid.extent.rows + 2 * GROUP_MARGIN,
    },
  };
};

/**
 * How many tracks a child actually gets, given its own size and the tracks its cell covers.
 *
 * Its own, for a child in a single cell. Sizing a track to the widest thing in it and stretching
 * everything else in that track to match are two different things, and only the first is what keeps
 * a group from being drawn over: a track is as wide as its widest occupant, so handing that width to
 * every occupant makes a leaf as big as the group beside it. `setNodeCellRect` draws a node at its
 * cell rather than centred in it, so that reached pixels - one loose component rendered as an empty
 * box exactly as tall as the four-child group next to it - and `adjustGridSizes` cannot recover it,
 * because its unspanned passes skip anything with a span over 1 and never let the leaf's own
 * minimum size its own track.
 *
 * The covered range, for a child whose cell spans several. There the span is the author's request
 * rather than a measurement, so it has to reach its last track; the `max` is for a group bigger
 * than the cells it was given, which must still enclose its own children.
 *
 * Parity holds either way. A track size is odd - `toTrackSizes` starts at `LEAF_EXTENT` and only
 * ever maxes with an extent or adds `extent - covered`, which is even - and an extent is odd by
 * construction, so both arms and their `max` are odd.
 */
const spanOf = (
  own: number,
  cells: number,
  covered: number,
  fills: boolean
): number => (cells === 1 && !fills ? own : Math.max(own, covered));

/** Half of a spare extent, rounded down to even so content stays on node tracks. */
const evenHalf = (spare: number): number =>
  Math.max(0, Math.floor(spare / 4) * 2);

export const toGlobalCells = <T>(
  root: ManualTabularNode<T>
): Map<T, GlobalCell> => {
  const cells = new Map<T, GlobalCell>();

  const place = (
    subject: Measured<T>,
    col: number,
    row: number,
    colSpan: number,
    rowSpan: number
  ): void => {
    cells.set(subject.source.node, { col, row, colSpan, rowSpan });
    if (subject.children.length === 0) return;

    const { grid } = subject;
    const centered = subject.source.centerContent === true;
    const contentCol =
      col +
      GROUP_MARGIN +
      (centered ? evenHalf(colSpan - subject.extent.cols) : 0);
    const contentRow =
      row +
      GROUP_MARGIN +
      (centered ? evenHalf(rowSpan - subject.extent.rows) : 0);

    for (const child of subject.children) {
      const cell = cellOf(child.source);
      const lastCol = cell.col + cell.colSpan - 1;
      const lastRow = cell.row + cell.rowSpan - 1;
      place(
        child,
        contentCol + grid.colStarts[cell.col],
        contentRow + grid.rowStarts[cell.row],
        spanOf(
          child.extent.cols,
          cell.colSpan,
          grid.colStarts[lastCol] +
            grid.colWidths[lastCol] -
            grid.colStarts[cell.col],
          cell.fill === 'across'
        ),
        spanOf(
          child.extent.rows,
          cell.rowSpan,
          grid.rowStarts[lastRow] +
            grid.rowHeights[lastRow] -
            grid.rowStarts[cell.row],
          cell.fill === 'down'
        )
      );
    }
  };

  const measured = measure(root);
  place(measured, 1, 1, measured.extent.cols, measured.extent.rows);

  return cells;
};
